/**
 * The sample-data route table.
 *
 * One entry per endpoint a Help & Tour reaches, mapping it to the fixtures.
 * Reads are searched, sorted and paged locally so the screens behave normally;
 * writes go to the simulator and never leave the browser.
 *
 * Adding a step that visits a new screen means adding its endpoint here — that
 * is the single place the tours' data contract lives.
 */

import { API_ENDPOINTS } from '@/utils/constants/end-points';

import {
  TOUR_ADMIN_MEMBERS,
  TOUR_CLIENTS,
  TOUR_DATABASES,
  TOUR_DNS_RECORDS,
  TOUR_PRIMARY_CLIENT,
  TOUR_REGIONS,
} from '../data/tour-sample-admin';
import {
  getTourApprovalDetail,
  getTourParameterApprovals,
  TOUR_APPROVALS_BY_TYPE,
  TOUR_WORKFLOW_STATUS,
} from '../data/tour-sample-approvals';
import {
  TOUR_AWARD_LIST_RECIPIENTS,
  TOUR_AWARD_LISTS,
  TOUR_AWARDS,
  TOUR_FAIR_VALUES,
  TOUR_FINAL_AWARDS,
  computeTourAchievementFactors,
  getTourFinalAwardAdjustmentSummary,
  getTourFinalAwardConditions,
  getTourFinalAwardSummary,
  TOUR_PRIMARY_AWARD,
  TOUR_PRIMARY_AWARD_LIST,
  TOUR_PRIMARY_FINAL_AWARD,
  toTourFinalAwardApprovalRow,
} from '../data/tour-sample-awards';
import {
  tourAwardLetterContent,
  tourDocumentObjectUrl,
  tourFinalAwardLetterContent,
  tourGenericDocumentPdf,
} from '../data/tour-sample-documents';
import {
  getTourGovernanceCase,
  TOUR_GOVERNANCE_ACTION_TYPES,
  TOUR_GOVERNANCE_AWARDS,
  TOUR_GOVERNANCE_CASES,
  TOUR_GOVERNANCE_EMPLOYEE_AWARDS,
  TOUR_GOVERNANCE_EMPLOYEES,
  TOUR_GOVERNANCE_SUMMARY,
  TOUR_GOVERNANCE_VESTING_SCHEDULES,
} from '../data/tour-sample-governance';
import { getTourLedgerPayload } from '../data/tour-sample-ledger';
import {
  getTourDashboardCounts,
  getTourPlanDocuments,
  TOUR_COUNTRIES,
  TOUR_EMPLOYEE_STATUSES,
  TOUR_MASTER_TEMPLATES,
  TOUR_MASTER_USERS,
  TOUR_STATUSES,
  TOUR_SYSTEM_MODULES,
  TOUR_TIMEZONES,
  TOUR_WORKFLOW_STATUSES,
} from '../data/tour-sample-master';
import {
  getTourParameterCounts,
  getTourParameterRows,
  getTourParameterSchema,
  getTourParameterValues,
  TOUR_DATA_ACCESS_LEVEL_TABLES,
  TOUR_PARAMETER_TABLES,
} from '../data/tour-sample-parameters';
import {
  TOUR_EMPLOYEES,
  TOUR_PRIMARY_EMPLOYEE,
  TOUR_USER_GROUPS,
  TOUR_USERS,
} from '../data/tour-sample-people';
import {
  simulateTourPolicy,
  TOUR_POLICY_MASTERS,
  TOUR_ACTIVE_POLICIES,
  TOUR_PRIMARY_POLICY,
  TOUR_ROUNDING_POLICIES,
} from '../data/tour-sample-policies';
import {
  getTourReportCounts,
  getTourReportDefinition,
  TOUR_BUSINESS_AREAS,
  getTourReportResult,
  TOUR_DOWNLOADED_REPORTS,
  TOUR_RECENTLY_VIEWED_REPORTS,
  TOUR_RECORD_TYPE_FIELDS,
  TOUR_RECORD_TYPES,
  TOUR_REPORT_DEFINITIONS,
  TOUR_REPORT_FILTERS,
  TOUR_REPORT_PREVIEW,
  TOUR_REPORTS,
} from '../data/tour-sample-reports';
import {
  getTourBatchDetail,
  TOUR_BATCH_RECIPIENTS,
  TOUR_COMBINED_SECURITY_EMAILS,
  TOUR_CHECKER_BATCH_STATUS_ID,
  TOUR_CREDITING_BATCHES,
  TOUR_CREDITING_DETAILS,
  TOUR_PRIMARY_SECURITY,
  TOUR_READY_AWARDS,
  TOUR_REGISTRAR_SUBMISSIONS,
  TOUR_REGISTRAR_VERIFICATIONS,
  TOUR_SECURITIES,
  TOUR_SIGNATORIES,
  TOUR_STAKEHOLDER_NOTIFICATIONS,
  TOUR_STOCK_PRICES,
  TOUR_TOTAL_TREASURY_SHARES,
} from '../data/tour-sample-share-credit';
import {
  TOUR_TEMPLATE_CATEGORIES,
  TOUR_TEMPLATE_FIELDS,
  TOUR_TEMPLATE_TYPES,
  TOUR_TEMPLATES,
} from '../data/tour-sample-templates';
import { applyTourDemoState } from '../state/tour-demo-state';
import { getTourFlowId } from '../tour-mode';

import {
  registerTourRoutes,
  TourRequestContext,
  TourRoute,
} from './tour-data-source';
import {
  simulateAcknowledge,
  simulateCheckerAction,
  simulateCreate,
  simulateDelete,
  simulateStatusChange,
  simulateUpdate,
} from './tour-mutations';
import { listTourData, searchTourData } from './tour-query';

const {
  ADMIN,
  AWARD,
  AWARDGOVERNANCE,
  AwardLetter,
  AwardList,
  CREDITDETAILS,
  DATABASE,
  FINALAWARD,
  MAINTENANCE,
  MASTER,
  ONBOARDING,
  PARAMETERS,
  PERSONALLEDGER,
  PLANDOCUMENTS,
  REPORT,
  REPORTCATALOG,
  ROUNDINGPOLICY,
  SHARECREDIT,
  TEMPLATE,
  TENANT,
  UserGroup,
  fileUpload,
} = API_ENDPOINTS;

/** Shorthand for a read route. */
const get = (pattern: string, resolve: TourRoute['resolve']): TourRoute => ({
  method: 'get',
  pattern,
  resolve,
});

const post = (pattern: string, resolve: TourRoute['resolve']): TourRoute => ({
  method: 'post',
  pattern,
  resolve,
});

const put = (pattern: string, resolve: TourRoute['resolve']): TourRoute => ({
  method: 'put',
  pattern,
  resolve,
});

const del = (pattern: string, resolve: TourRoute['resolve']): TourRoute => ({
  method: 'delete',
  pattern,
  resolve,
});

/** A collection as the tour currently sees it, demo-state edits included. */
const live = <T extends Record<string, any>>(
  collection: string,
  rows: readonly T[],
  idKey: keyof T
): T[] => applyTourDemoState(collection, rows, idKey);

/* -------------------------------------------------------------------------- */
/* Users, groups and the employee pickers                                     */
/* -------------------------------------------------------------------------- */

const userRoutes: TourRoute[] = [
  get(TENANT, ctx => {
    const statusId = ctx.query.get('statusId');
    const groupId = ctx.query.get('userGroupId');
    let rows = live('users', TOUR_USERS, 'id');
    if (statusId)
      rows = rows.filter(user => String(user.statusId) === statusId);
    if (groupId)
      rows = rows.filter(user => String(user.userGroupId) === groupId);
    return listTourData(rows, ctx.query, {
      searchKeys: ['fullName', 'employeeId', 'email', 'userGroupName'],
    });
  }),
  get(`${TENANT}/:userId`, ctx => {
    const rows = live('users', TOUR_USERS, 'id');
    return rows.find(user => String(user.id) === ctx.params.userId) ?? rows[0];
  }),
  post(
    TENANT,
    simulateCreate('users', 'employeeId', 'User sent for approval.')
  ),
  put(`${TENANT}/status/:userId`, simulateStatusChange('users', 'userId')),
  put(
    `${TENANT}/:userId`,
    simulateUpdate('users', 'userId', 'User sent for approval.')
  ),
  del(`${TENANT}/:userId`, simulateDelete('users', 'userId', 'User deleted.')),
  post(`${TENANT}/checker-action`, simulateCheckerAction('userApprovals')),
  put(`${TENANT}/revision/:txnId`, simulateAcknowledge('Revision submitted.')),
  get(`${TENANT}/revision/:txnId`, ctx =>
    getTourApprovalDetail(ctx.params.txnId)
  ),

  get(UserGroup, ctx =>
    listTourData(live('userGroups', TOUR_USER_GROUPS, 'id'), ctx.query, {
      searchKeys: ['groupName', 'dataAccessLevels'],
    })
  ),
  get(`${UserGroup}/:groupId`, ctx => {
    const rows = live('userGroups', TOUR_USER_GROUPS, 'id');
    const group =
      rows.find(item => String(item.id) === ctx.params.groupId) ?? rows[0];
    return {
      ...group,
      users: TOUR_USERS.filter(user => user.userGroupId === group.id),
    };
  }),
  post(
    UserGroup,
    simulateCreate('userGroups', 'groupName', 'User group sent for approval.')
  ),
  put(
    `${UserGroup}/status/:groupId`,
    simulateStatusChange('userGroups', 'groupId')
  ),
  put(
    `${UserGroup}/:groupId`,
    simulateUpdate('userGroups', 'groupId', 'User group sent for approval.')
  ),
  del(
    `${UserGroup}/:groupId`,
    simulateDelete('userGroups', 'groupId', 'User group deleted.')
  ),
  post(
    `${UserGroup}/checker-action`,
    simulateCheckerAction('userGroupApprovals')
  ),
  get(`${UserGroup}/revision/:txnId`, ctx =>
    getTourApprovalDetail(ctx.params.txnId)
  ),
];

/* -------------------------------------------------------------------------- */
/* Master lookups                                                             */
/* -------------------------------------------------------------------------- */

const masterRoutes: TourRoute[] = [
  get(`${MASTER}/dashboard-counts`, () => getTourDashboardCounts()),
  get(`${MASTER}/status`, () => TOUR_STATUSES),
  get(`${MASTER}/employee-status`, () => TOUR_EMPLOYEE_STATUSES),
  get(`${MASTER}/users`, ctx =>
    searchTourData(
      [...TOUR_MASTER_USERS],
      ctx.query.get('SearchField') ?? ctx.query.get('searchField')
    )
  ),
  get(`${MASTER}/templates`, () => TOUR_MASTER_TEMPLATES),
  // These four are reached from redux slices rather than the service layer.
  // `user-groups` answers with a bare array the User Management filter runs
  // `.find()` over, so the shape matters as much as the presence.
  get(`${MASTER}/user-groups`, () => TOUR_USER_GROUPS),
  get(`${MASTER}/system-modules/permissions`, () => TOUR_SYSTEM_MODULES),
  get(`${MASTER}/workflow-statuses`, () => TOUR_WORKFLOW_STATUSES),
  get(`${MASTER}/template-categories`, () => TOUR_TEMPLATE_CATEGORIES),
  get(`${MASTER}/signatories`, () => TOUR_SIGNATORIES),
  get(
    `${MASTER}/combined-security-emails`,
    () => TOUR_COMBINED_SECURITY_EMAILS
  ),
  // Permissions drive which tours are even offered, so the tour keeps whatever
  // the signed-in user really has — see the note in tour-api-bridge.ts.
];

/* -------------------------------------------------------------------------- */
/* Parameters                                                                 */
/* -------------------------------------------------------------------------- */

const parameterRoutes: TourRoute[] = [
  get(PARAMETERS, ctx =>
    ctx.query.get('isDataAccessLevel') === 'true'
      ? TOUR_DATA_ACCESS_LEVEL_TABLES
      : TOUR_PARAMETER_TABLES
  ),
  post(`${PARAMETERS}/values`, ctx => {
    const body = (ctx.body ?? {}) as { tableNames?: string[] };
    return getTourParameterValues(body.tableNames ?? []);
  }),
  get(`${PARAMETERS}/schema/:tableName`, ctx =>
    getTourParameterSchema(ctx.params.tableName)
  ),
  get(`${PARAMETERS}/fields/:fieldId/options`, () =>
    TOUR_STATUSES.map(status => ({
      value: String(status.id),
      label: status.name,
    }))
  ),
  get(`${PARAMETERS}/:tableName`, ctx => {
    const table = ctx.params.tableName;
    const rows = live(`parameters:${table}`, getTourParameterRows(table), 'id');
    const page = listTourData(rows, ctx.query);
    // This list answers with `data` rather than `items` (ParameterListResponse).
    return {
      data: page.items,
      totalCount: page.totalCount,
      offset: page.offset,
      limit: page.limit,
    };
  }),
  get(`${PARAMETERS}/:tableName/:id`, ctx => {
    const rows = getTourParameterRows(ctx.params.tableName);
    return rows.find(row => String(row.id) === ctx.params.id) ?? rows[0] ?? {};
  }),
  post(
    `${PARAMETERS}/checker-action`,
    simulateCheckerAction('parameterApprovals')
  ),
  post(`${PARAMETERS}/:tableName`, ctx =>
    simulateCreate(
      `parameters:${ctx.params.tableName}`,
      'id',
      'Record sent for approval.'
    )(ctx)
  ),
  put(
    `${PARAMETERS}/revision/:txnId`,
    simulateAcknowledge('Revision submitted.')
  ),
  put(`${PARAMETERS}/:tableName/:id/status`, ctx =>
    simulateStatusChange(`parameters:${ctx.params.tableName}`, 'id')(ctx)
  ),
  put(`${PARAMETERS}/:tableName/:id`, ctx =>
    simulateUpdate(
      `parameters:${ctx.params.tableName}`,
      'id',
      'Record sent for approval.'
    )(ctx)
  ),
];

/* -------------------------------------------------------------------------- */
/* Maker-checker (dashboard) lists and details                                */
/* -------------------------------------------------------------------------- */

/** Status names the fixtures use, by the workflow status id they stand for. */
const WORKFLOW_STATUS_ID_BY_NAME = new Map<string, number>([
  // A draft is still with its maker.
  ['draft', TOUR_WORKFLOW_STATUS.makerInProgress],
  ['maker in progress', TOUR_WORKFLOW_STATUS.makerInProgress],
  ['pending checker approval', TOUR_WORKFLOW_STATUS.pendingCheckerApproval],
  ['pending revision', TOUR_WORKFLOW_STATUS.pendingRevision],
  ['approved', TOUR_WORKFLOW_STATUS.approved],
  ['rejected', TOUR_WORKFLOW_STATUS.rejected],
]);

/**
 * Whether a record with this status belongs in the Maker / Checker bucket the
 * request asks for (`WorkflowStatusId`, ids joined by commas). With no filter
 * the pending-approvals lists keep their checker queue, as before.
 */
const inRequestedBucket = (
  statusName: string | null | undefined,
  query: URLSearchParams
) => {
  const requested =
    query.get('WorkflowStatusId') ?? query.get('workflowStatusId');
  const statusId = WORKFLOW_STATUS_ID_BY_NAME.get(
    (statusName ?? '').trim().toLowerCase()
  );
  if (!requested) {
    return statusId === TOUR_WORKFLOW_STATUS.pendingCheckerApproval;
  }
  return (
    statusId !== undefined &&
    requested
      .split(',')
      .map(value => Number(value.trim()))
      .includes(statusId)
  );
};

/**
 * The demo-state collection each approval list reads, matching the one its
 * checker action writes, so a record approved or rejected in a tour leaves the
 * list it came from.
 */
const APPROVAL_STATE_KEY_BY_TYPE = new Map<string, string>([
  ['users', 'userApprovals'],
  ['user-groups', 'userGroupApprovals'],
  ['awards', 'awardApprovals'],
  ['templates', 'templateApprovals'],
]);

const maintenanceRoutes: TourRoute[] = [
  get(`${MAINTENANCE}/parameters/counts`, () => getTourParameterCounts()),
  get(`${MAINTENANCE}/parameters/:tableName`, ctx =>
    listTourData(getTourParameterApprovals(ctx.params.tableName), ctx.query)
  ),
  // One route serves every module's list, so each is filtered by the Maker /
  // Checker bucket. Literal per-module routes would outrank this capture and
  // skip the filter — a "Maker in progress" row then showed up under Pending
  // Checker Approval.
  get(`${MAINTENANCE}/:type`, ctx => {
    const type = ctx.params.type.toLowerCase();
    const rows = TOUR_APPROVALS_BY_TYPE.get(type);
    if (!rows) {
      // Not an approval list — the route also serves a transaction detail
      // whose id happens to occupy the same segment.
      return getTourApprovalDetail(ctx.params.type);
    }
    const statusId =
      ctx.query.get('workflowStatusId') ?? ctx.query.get('WorkflowStatusId');
    const wanted = statusId
      ? new Set(statusId.split(',').map(value => value.trim()))
      : null;
    // Demo state first: an approval the user just actioned leaves its bucket.
    const current = live(
      APPROVAL_STATE_KEY_BY_TYPE.get(type) ?? `${type}Approvals`,
      rows,
      'txnId'
    );
    return listTourData(
      wanted
        ? current.filter(row => wanted.has(String(row.workflowStatusId)))
        : current,
      ctx.query
    );
  }),
];

/* -------------------------------------------------------------------------- */
/* Awards                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Find a sample award by whatever identifier the caller has.
 *
 * The View Awards row click navigates with `performanceOfferId`, and the
 * detail page then passes that straight through as the `awardNo` path segment
 * — so a lookup by award number alone misses every record.
 */
const findTourAward = (identifier: string) => {
  const rows = live('awards', TOUR_AWARDS, 'awardNo');
  return (
    rows.find(
      award =>
        award.awardNo === identifier ||
        String(award.performanceOfferId) === identifier
    ) ?? TOUR_PRIMARY_AWARD
  );
};

const awardRoutes: TourRoute[] = [
  get(AWARD, ctx =>
    listTourData(live('awards', TOUR_AWARDS, 'awardNo'), ctx.query, {
      searchKeys: ['awardNo', 'employeeName', 'planName', 'awardType'],
    })
  ),
  get(`${AWARD}/:awardNo`, ctx => {
    const rows = live('awards', TOUR_AWARDS, 'awardNo');
    return rows.find(award => award.awardNo === ctx.params.awardNo) ?? rows[0];
  }),
  post(AWARD, simulateCreate('awards', 'awardNo', 'Award sent for approval.')),
  put(
    `${AWARD}/revise/:txnId`,
    simulateAcknowledge('Award revision submitted.')
  ),
  put(
    `${AWARD}/:awardNo`,
    simulateUpdate('awards', 'awardNo', 'Award sent for approval.')
  ),
  del(
    `${AWARD}/:awardNo`,
    simulateDelete('awards', 'awardNo', 'Award deleted.')
  ),
  post(`${AWARD}/checker-action`, simulateCheckerAction('awardApprovals')),

  // View Awards / Grants — the participant-facing list and record.
  get(API_ENDPOINTS.VIEW, ctx => {
    const employee =
      ctx.query.get('SearchEmployee') ?? ctx.query.get('searchEmployee');
    const awardNo = ctx.query.get('SearchTerm') ?? ctx.query.get('AwardNo');
    const planType = ctx.query.get('PlanType');

    let rows = live('awards', TOUR_AWARDS, 'awardNo');
    if (employee) {
      rows = searchTourData(rows, employee, ['employeeName', 'employeeId']);
    }
    if (awardNo) rows = searchTourData(rows, awardNo, ['awardNo']);
    if (planType) {
      rows = rows.filter(award => String(award.planTypeId) === planType);
    }
    return listTourData(rows, ctx.query, {
      searchKeys: ['awardNo', 'employeeName', 'planName', 'statusName'],
    });
  }),
  get(`${API_ENDPOINTS.VIEW}/:awardNo/letter`, ctx =>
    tourAwardLetterContent(findTourAward(ctx.params.awardNo))
  ),
  get(`${API_ENDPOINTS.VIEW}/:awardNo`, ctx =>
    findTourAward(ctx.params.awardNo)
  ),
  post(`${API_ENDPOINTS.VIEW}/:awardNo/respond`, ctx => {
    const body = (ctx.body ?? {}) as { acceptanceType?: string };
    const accepted = body.acceptanceType === 'A';
    // The path carries the offer id, but the awards collection is keyed by
    // award number — patch against the record we actually resolve.
    return simulateUpdate(
      'awards',
      'awardNo',
      accepted ? 'Award accepted.' : 'Award declined.'
    )({
      ...ctx,
      params: { awardNo: findTourAward(ctx.params.awardNo).awardNo },
      body: {
        actionStatus: accepted ? 'Accepted' : 'Declined',
        statusName: accepted ? 'Accepted' : 'Declined',
        respondedOn: new Date().toISOString().slice(0, 10),
        isAcceptAllowedAction: false,
        isRejectAllowedAction: false,
      },
    });
  }),

  // Fair value
  post('/AwardManagement/FairValue/Search', ctx => {
    const body = (ctx.body ?? {}) as {
      searchField?: string;
      pagination?: { offset?: number; limit?: number };
    };
    const rows = searchTourData(
      live('fairValues', TOUR_FAIR_VALUES, 'id'),
      body.searchField
    );
    const limit = body.pagination?.limit ?? 10;
    const offset = body.pagination?.offset ?? 0;
    return {
      items: rows.slice(offset * limit, offset * limit + limit),
      totalCount: rows.length,
      offset,
      limit,
      totalPages: Math.max(1, Math.ceil(rows.length / limit)),
    };
  }),
  put(
    '/AwardManagement/FairValue/Edit',
    simulateAcknowledge('Fair value saved.')
  ),
];

/* -------------------------------------------------------------------------- */
/* Award lists (uploads) and award letters                                    */
/* -------------------------------------------------------------------------- */

const awardListRoutes: TourRoute[] = [
  get(AwardList, ctx =>
    listTourData(live('awardLists', TOUR_AWARD_LISTS, 'fileId'), ctx.query, {
      searchKeys: ['fileName', 'awardNo', 'awardType', 'statusName'],
    })
  ),
  get(`${AwardList}/pending-approval`, ctx =>
    listTourData(
      live('awardLists', TOUR_AWARD_LISTS, 'fileId').filter(
        list => list.status === 'Pending Checker Approval'
      ),
      ctx.query
    )
  ),
  get(`${AwardList}/sample-template`, () =>
    tourGenericDocumentPdf('Award List Upload Template')
  ),
  get(`${AwardList}/:fileId/details`, ctx =>
    listTourData(
      live('awardListRecipients', TOUR_AWARD_LIST_RECIPIENTS, 'id'),
      ctx.query,
      { searchKeys: ['employeeName', 'employeeId', 'awardNo'] }
    )
  ),
  get(`${AwardList}/:fileId/excludeditems`, () =>
    TOUR_AWARD_LIST_RECIPIENTS.filter(recipient => recipient.isExcluded)
  ),
  get(`${AwardList}/:fileId/summary`, ctx => ({
    ...TOUR_PRIMARY_AWARD_LIST,
    fileId: ctx.params.fileId,
    items: TOUR_AWARD_LIST_RECIPIENTS,
    totalCount: TOUR_AWARD_LIST_RECIPIENTS.length,
  })),
  get(`${AwardList}/:fileId/summary/review`, ctx => ({
    ...TOUR_PRIMARY_AWARD_LIST,
    fileId: ctx.params.fileId,
    items: TOUR_AWARD_LIST_RECIPIENTS,
    totalCount: TOUR_AWARD_LIST_RECIPIENTS.length,
  })),
  get(`${AwardList}/:fileId`, ctx => {
    const rows = live('awardLists', TOUR_AWARD_LISTS, 'fileId');
    return rows.find(list => list.fileId === ctx.params.fileId) ?? rows[0];
  }),
  post(
    `${AwardList}/upload`,
    simulateAcknowledge('File uploaded for validation.')
  ),
  post(
    `${AwardList}/:fileId/reupload`,
    simulateAcknowledge('File re-uploaded.')
  ),
  post(
    `${AwardList}/:fileId/exclude`,
    simulateAcknowledge('Records excluded.')
  ),
  post(
    `${AwardList}/:fileId/submit`,
    simulateAcknowledge('Award list submitted.')
  ),
  post(
    `${AwardList}/:fileId/send-for-approval`,
    simulateAcknowledge('Sent for approval.')
  ),
  post(
    `${AwardList}/:fileId/approve-award-list`,
    simulateAcknowledge('Award list approved.')
  ),
  get(`${AwardList}/:fileId/approved-details-export`, () =>
    tourGenericDocumentPdf('Approved Award List')
  ),
  del(
    `${AwardList}/:fileId`,
    simulateDelete('awardLists', 'fileId', 'Award list deleted.')
  ),

  get(`${AwardLetter}/list`, ctx =>
    listTourData(live('awardLists', TOUR_AWARD_LISTS, 'fileId'), ctx.query, {
      searchKeys: ['fileName', 'awardNo', 'letterStatus'],
    })
  ),
  // `preview` / `final-preview` are the wizard's paged RECIPIENT lists; only
  // the `template-preview` routes return the letter itself.
  get(`${AwardLetter}/preview`, ctx =>
    listTourData(
      live('awardListRecipients', TOUR_AWARD_LIST_RECIPIENTS, 'id'),
      ctx.query,
      { searchKeys: ['employeeName', 'employeeId', 'awardNo'] }
    )
  ),
  get(`${AwardLetter}/final-preview`, ctx =>
    listTourData(
      live('awardListRecipients', TOUR_AWARD_LIST_RECIPIENTS, 'id'),
      ctx.query,
      { searchKeys: ['employeeName', 'employeeId', 'awardNo'] }
    )
  ),
  get(`${AwardLetter}/template-preview`, () =>
    tourAwardLetterContent(TOUR_PRIMARY_AWARD)
  ),
  get(`${AwardLetter}/summary`, () => ({
    ...TOUR_PRIMARY_AWARD_LIST,
    recipients: TOUR_AWARD_LIST_RECIPIENTS,
    items: TOUR_AWARD_LIST_RECIPIENTS,
    totalCount: TOUR_AWARD_LIST_RECIPIENTS.length,
    suppressedCount: 0,
  })),
  get(`${AwardLetter}/:awardNo/summary-final`, () => ({
    ...TOUR_PRIMARY_FINAL_AWARD,
    recipients: TOUR_AWARD_LIST_RECIPIENTS,
    items: TOUR_AWARD_LIST_RECIPIENTS,
    totalCount: TOUR_AWARD_LIST_RECIPIENTS.length,
  })),
  get(`${AwardLetter}/:awardNo/template-preview-final`, () =>
    tourFinalAwardLetterContent(TOUR_PRIMARY_FINAL_AWARD)
  ),
  post(
    `${AwardLetter}/:id/generate`,
    simulateAcknowledge('Award letters generated.')
  ),
  post(
    `${AwardLetter}/:id/generate-final`,
    simulateAcknowledge('Final award letters generated.')
  ),
  post(
    `${AwardLetter}/:id/suppress`,
    simulateAcknowledge('Recipients suppressed.')
  ),
  post(
    `${AwardLetter}/:id/suppress-final`,
    simulateAcknowledge('Recipients suppressed.')
  ),
];

/* -------------------------------------------------------------------------- */
/* Final award                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Whether the summary should come back as already sent for approval — the
 * achievement factor, conditions and letter filled in, which is what the
 * checker's review page shows. True for an award awaiting approval, except in
 * the maker's own Final Award tours (the standalone one and the award-flow
 * chapter): that wizard seeds itself from the same
 * summary and has to open uncomputed for the user to work through.
 */
const FINAL_AWARD_MAKER_TOURS = new Set([
  'final-award',
  'award-flow-final-award',
]);

const isSubmittedFinalAward = (awardNo: string) =>
  !FINAL_AWARD_MAKER_TOURS.has(getTourFlowId() ?? '') &&
  live('finalAwards', TOUR_FINAL_AWARDS, 'awardNo').some(
    award => award.awardNo === awardNo && award.status === 'Pending Approval'
  );

const finalAwardRoutes: TourRoute[] = [
  get(FINALAWARD, ctx =>
    listTourData(live('finalAwards', TOUR_FINAL_AWARDS, 'awardNo'), ctx.query, {
      searchKeys: ['awardNo', 'employeeName', 'planName', 'statusName'],
    })
  ),
  // Shaped as the backend's list item: the Administration Hub table reads
  // its plan type, awardees, letter, maker, date and status from these.
  get(`${FINALAWARD}/approvallist`, ctx => {
    // The filter drawer can ask for Pending Revision; no sample award is in it.
    const statusId = ctx.query.get('StatusId');
    const rows = live('finalAwards', TOUR_FINAL_AWARDS, 'awardNo')
      .filter(award => award.status === 'Pending Approval')
      .map(toTourFinalAwardApprovalRow)
      .filter(row => !statusId || String(row.statusId) === statusId);
    return listTourData(rows, ctx.query, {
      searchKeys: ['awardNo', 'awardType', 'makerName'],
    });
  }),
  get(`${FINALAWARD}/:awardNo/summary`, ctx => ({
    ...getTourFinalAwardSummary(ctx.params.awardNo, {
      submitted: isSubmittedFinalAward(ctx.params.awardNo),
    }),
    items: live('finalAwards', TOUR_FINAL_AWARDS, 'awardNo'),
    totalCount: TOUR_FINAL_AWARDS.length,
  })),
  get(`${FINALAWARD}/:awardNo/summary/view`, ctx =>
    getTourFinalAwardSummary(ctx.params.awardNo, {
      submitted: isSubmittedFinalAward(ctx.params.awardNo),
    })
  ),
  get(`${FINALAWARD}/:awardNo/adjustment-summary`, ctx =>
    getTourFinalAwardAdjustmentSummary(ctx.params.awardNo)
  ),
  get(`${FINALAWARD}/:awardNo/conditions`, ctx =>
    getTourFinalAwardConditions(ctx.params.awardNo)
  ),
  get(`${FINALAWARD}/:awardNo/preview-file`, () =>
    tourFinalAwardLetterContent(TOUR_PRIMARY_FINAL_AWARD)
  ),
  get(`${FINALAWARD}/:awardNo/review/:txnId`, ctx => {
    const rows = live('finalAwards', TOUR_FINAL_AWARDS, 'awardNo');
    const award =
      rows.find(item => item.awardNo === ctx.params.awardNo) ?? rows[0];
    return { ...award, items: rows, totalCount: rows.length };
  }),
  get(`${FINALAWARD}/:awardNo/download/:fileType`, () =>
    tourDocumentObjectUrl('Final Award Letter')
  ),
  post(`${FINALAWARD}/:awardNo/compute`, ctx => {
    // The stage renders the returned factors, so it needs the computed values
    // back rather than an acknowledgement.
    const body = (ctx.body ?? {}) as {
      conditions?: { conditionId: number; achievement: number }[];
    };
    const achievements = Object.fromEntries(
      (body.conditions ?? []).map(condition => [
        condition.conditionId,
        Number(condition.achievement),
      ])
    );
    return computeTourAchievementFactors(achievements);
  }),
  post(`${FINALAWARD}/:awardNo/proceed`, ctx => ({
    // The stage checks `success` before moving on.
    success: true,
    ...(simulateAcknowledge('Achievement factors saved successfully.')(
      ctx
    ) as object),
  })),
  post(
    `${FINALAWARD}/:awardNo/send-for-approval`,
    simulateAcknowledge('Sent for approval.')
  ),
  post(`${FINALAWARD}/:awardNo/action`, simulateCheckerAction('finalAwards')),
  post(
    `${FINALAWARD}/:awardNo/upload-adjustment`,
    simulateAcknowledge('Adjustments uploaded.')
  ),
  post(
    `${FINALAWARD}/:awardNo/upload-serviceday`,
    simulateAcknowledge('Service days uploaded.')
  ),
  del(
    `${FINALAWARD}/:awardNo/discard/:txnId`,
    simulateAcknowledge('Draft discarded.')
  ),
];

/* -------------------------------------------------------------------------- */
/* Templates                                                                  */
/* -------------------------------------------------------------------------- */

const templateRoutes: TourRoute[] = [
  get(`${TEMPLATE}/categories/:categoryId/fields`, () => TOUR_TEMPLATE_FIELDS),
  get(`${TEMPLATE}/categories`, () => TOUR_TEMPLATE_CATEGORIES),
  get(`${TEMPLATE}/types`, () => TOUR_TEMPLATE_TYPES),
  get(TEMPLATE, ctx => {
    const categoryId = ctx.query.get('categoryId');
    const typeId = ctx.query.get('TemplateTypeId');
    let rows = live('templates', TOUR_TEMPLATES, 'templateId');
    if (categoryId) {
      rows = rows.filter(row => String(row.categoryId) === categoryId);
    }
    if (typeId) {
      rows = rows.filter(row => String(row.templateTypeId) === typeId);
    }
    return listTourData(rows, ctx.query, {
      searchKeys: ['templateName', 'categoryName', 'statusName'],
    });
  }),
  get(`${TEMPLATE}/revision/:txnId`, ctx =>
    getTourApprovalDetail(ctx.params.txnId)
  ),
  get(`${TEMPLATE}/:templateId`, ctx => {
    const rows = live('templates', TOUR_TEMPLATES, 'templateId');
    return (
      rows.find(row => String(row.templateId) === ctx.params.templateId) ??
      rows[0]
    );
  }),
  post(
    TEMPLATE,
    simulateCreate('templates', 'templateName', 'Template sent for approval.')
  ),
  post(
    `${TEMPLATE}/checker-action`,
    simulateCheckerAction('templateApprovals')
  ),
  post(
    `${TEMPLATE}/:templateId/send-email`,
    simulateAcknowledge('Test email sent.')
  ),
  put(
    `${TEMPLATE}/status/:templateId`,
    simulateStatusChange('templates', 'templateId')
  ),
  put(
    `${TEMPLATE}/:templateId`,
    simulateUpdate('templates', 'templateId', 'Template sent for approval.')
  ),
  del(
    `${TEMPLATE}/:templateId`,
    simulateDelete('templates', 'templateId', 'Template deleted.')
  ),
];

/* -------------------------------------------------------------------------- */
/* Personal ledger                                                            */
/* -------------------------------------------------------------------------- */

const ledgerRoutes: TourRoute[] = [
  get(PERSONALLEDGER, ctx => {
    const ledgerType = Number(ctx.query.get('LedgerType') ?? 1);
    const userField = ctx.query.get('UserField');
    const employee =
      TOUR_EMPLOYEES.find(
        item => String(item.id) === userField || item.employeeId === userField
      ) ?? TOUR_PRIMARY_EMPLOYEE;

    const payload = getTourLedgerPayload(ledgerType, employee) as Record<
      string,
      any
    >;
    const rowsKey = Object.keys(payload).find(key => key.startsWith('ledger'));
    const rows: Record<string, any>[] = rowsKey ? payload[rowsKey] : [];
    const page = listTourData(rows, ctx.query);

    return {
      ...payload,
      [rowsKey ?? 'ledgerAwardDetail']: page.items,
      pagination: {
        offset: page.offset,
        limit: page.limit,
        totalCount: page.totalCount,
        totalPages: page.totalPages,
      },
    };
  }),
];

/* -------------------------------------------------------------------------- */
/* Rounding policies                                                          */
/* -------------------------------------------------------------------------- */

/** Rounding policies as the tour currently sees them, its own creates included. */
const livePolicies = () => live('policies', TOUR_ROUNDING_POLICIES, 'policyId');

/** Policies created in a tour are numbered clear of the sample ones. */
const TOUR_CREATED_POLICY_ID_BASE = 900_000;
let createdPolicyCount = 0;

const createPolicy = simulateCreate(
  'policies',
  'policyName',
  'Policy sent for approval.'
);

const policyRoutes: TourRoute[] = [
  get(`${ROUNDINGPOLICY}/Search`, ctx => {
    const rows = searchTourData(livePolicies(), ctx.query.get('SearchField'), [
      'policyName',
      'planTypeName',
      'policyStatusName',
    ]);
    const limit = Number(ctx.query.get('Pagination.Limit') ?? 10);
    const offset = Number(ctx.query.get('Pagination.Offset') ?? 0);
    return {
      roundingPolicies: rows.slice(offset * limit, offset * limit + limit),
      pagination: {
        offset,
        limit,
        isExport: false,
        totalCount: rows.length,
        totalPages: Math.max(1, Math.ceil(rows.length / limit)),
        sortBy: ctx.query.get('Pagination.SortBy'),
        sortDir: ctx.query.get('Pagination.SortDir'),
      },
    };
  }),
  // A POST despite being a read: the filters arrive as a body, not a query.
  post(`${ROUNDINGPOLICY}/Simulation/Details`, ctx => {
    const body = (ctx.body ?? {}) as Record<string, unknown>;
    return simulateTourPolicy({
      policyId: (body.policyId as number) ?? TOUR_PRIMARY_POLICY.policyId,
      stageLevelId: (body.stageLevelId as number) ?? 0,
      fieldLevelId: (body.fieldLevelId as number) ?? 0,
      inputValue: (body.inputValue as number) ?? 0,
    });
  }),
  // The simulation picker reads whole policies and derives its levels from them.
  get(`${ROUNDINGPOLICY}/List`, () => ({
    roundingPolicies: TOUR_ACTIVE_POLICIES,
    pagination: {
      offset: 0,
      limit: TOUR_ACTIVE_POLICIES.length,
      isExport: false,
      totalCount: TOUR_ACTIVE_POLICIES.length,
      totalPages: 1,
      sortBy: null,
      sortDir: null,
    },
  })),
  get(`${ROUNDINGPOLICY}/pending-approvals`, ctx =>
    listTourData(
      livePolicies().filter(policy =>
        inRequestedBucket(policy.policyStatusName, ctx.query)
      ),
      ctx.query
    )
  ),
  // One transaction holds one policy, but the endpoint still wraps it in the
  // paged shape /Search uses.
  get(`${ROUNDINGPOLICY}/txn/:txnId`, ctx => {
    const policy =
      livePolicies().find(item => item.txnId === ctx.params.txnId) ??
      TOUR_ROUNDING_POLICIES[3];
    return {
      roundingPolicies: [policy],
      pagination: {
        offset: 0,
        limit: 10,
        isExport: false,
        totalCount: 1,
        totalPages: 1,
        sortBy: null,
        sortDir: null,
      },
    };
  }),
  get(ROUNDINGPOLICY, () => TOUR_POLICY_MASTERS),
  // A new policy carries what the policy lists key and filter on, so it
  // queues for approval and the checker can open and decide on it.
  post(ROUNDINGPOLICY, ctx => {
    createdPolicyCount += 1;
    return createPolicy({
      ...ctx,
      body: {
        ...(ctx.body as Record<string, unknown>),
        policyId: TOUR_CREATED_POLICY_ID_BASE + createdPolicyCount,
        txnId: `TOUR-TXN-POL-NEW-${createdPolicyCount}`,
        policyStatusName: 'Pending Checker Approval',
      },
    });
  }),
  // Policies are keyed by policyId, and their pending list reads
  // policyStatusName, so the decision is filed against both.
  post(
    `${ROUNDINGPOLICY}/checker-action`,
    simulateCheckerAction('policies', {
      recordIdFor: txnId =>
        livePolicies().find(policy => policy.txnId === txnId)?.policyId,
      statusField: 'policyStatusName',
    })
  ),
  put(
    `${ROUNDINGPOLICY}/revise/:txnId`,
    simulateAcknowledge('Policy revision submitted.')
  ),
  put(
    `${ROUNDINGPOLICY}/:policyId`,
    simulateUpdate('policies', 'policyId', 'Policy sent for approval.')
  ),
  del(
    `${ROUNDINGPOLICY}/:policyId`,
    simulateDelete('policies', 'policyId', 'Policy deleted.')
  ),
];

/* -------------------------------------------------------------------------- */
/* Share crediting                                                            */
/* -------------------------------------------------------------------------- */

const shareCreditRoutes: TourRoute[] = [
  get(`${SHARECREDIT}/securities`, () => TOUR_SECURITIES),
  get(`${SHARECREDIT}/ready-awards`, ctx => {
    const rows = searchTourData(
      [...TOUR_READY_AWARDS],
      ctx.query.get('Search'),
      ['employeeName', 'employeeId', 'awardNo', 'planName']
    );
    const page = listTourData(rows, ctx.query);
    return {
      items: page.items,
      totalCount: page.totalCount,
      totalAvailableTreasuryShares: TOUR_TOTAL_TREASURY_SHARES,
    };
  }),
  get(`${SHARECREDIT}/checker/batches`, ctx =>
    listTourData(
      live('batches', TOUR_CREDITING_BATCHES, 'batchNo').filter(
        batch => batch.statusId === TOUR_CHECKER_BATCH_STATUS_ID
      ),
      ctx.query
    )
  ),
  get(`${SHARECREDIT}/checker/batches/:batchId/reviews`, () => ({
    reviews: TOUR_REGISTRAR_VERIFICATIONS,
    items: TOUR_REGISTRAR_VERIFICATIONS,
    totalCount: TOUR_REGISTRAR_VERIFICATIONS.length,
  })),
  get(`${SHARECREDIT}/batches`, ctx =>
    listTourData(
      live('batches', TOUR_CREDITING_BATCHES, 'batchNo'),
      ctx.query,
      {
        searchKeys: ['batchName', 'batchReference', 'statusName'],
      }
    )
  ),
  get(`${SHARECREDIT}/batches/:batchNo/details`, ctx =>
    getTourBatchDetail(ctx.params.batchNo)
  ),
  get(`${SHARECREDIT}/batches/:batchNo/review`, ctx =>
    getTourBatchDetail(ctx.params.batchNo)
  ),
  get(`${SHARECREDIT}/batches/:batchNo/crediting`, () => ({
    items: TOUR_BATCH_RECIPIENTS,
    totalCount: TOUR_BATCH_RECIPIENTS.length,
  })),
  get(
    `${SHARECREDIT}/batches/:batchNo/registrar-submissions`,
    () => TOUR_REGISTRAR_SUBMISSIONS
  ),
  get(
    `${SHARECREDIT}/batches/:batchNo/registrar-verifications`,
    () => TOUR_REGISTRAR_VERIFICATIONS
  ),
  get(
    `${SHARECREDIT}/batches/:batchNo/stakeholder-notifications`,
    () => TOUR_STAKEHOLDER_NOTIFICATIONS
  ),
  get(`${SHARECREDIT}/batches/:batchNo`, ctx =>
    getTourBatchDetail(ctx.params.batchNo)
  ),
  post(`${SHARECREDIT}/batch-summary`, () => ({
    ...getTourBatchDetail(TOUR_CREDITING_BATCHES[0].batchNo),
    security: TOUR_PRIMARY_SECURITY,
  })),
  post(
    `${SHARECREDIT}/batches`,
    simulateCreate('batches', 'batchName', 'Batch generated.')
  ),
  post(
    `${SHARECREDIT}/batches/:batchId/submission`,
    simulateAcknowledge('Batch submitted for verification.')
  ),
  post(
    `${SHARECREDIT}/batches/:batchId/registrar-submissions`,
    simulateAcknowledge('Sent to the share registrar.')
  ),
  post(
    `${SHARECREDIT}/batches/:batchId/registrar-verifications`,
    simulateAcknowledge('Verification recorded.')
  ),
  post(
    `${SHARECREDIT}/batches/:batchId/stakeholder-notifications`,
    simulateAcknowledge('Notifications sent.')
  ),
  post(
    `${SHARECREDIT}/batches/:batchId/restart`,
    simulateAcknowledge('Batch restarted.')
  ),
  put(
    `${SHARECREDIT}/batches/:batchId/details`,
    simulateUpdate('batches', 'batchId', 'Batch details saved.')
  ),
  del(
    `${SHARECREDIT}/batches/:batchId`,
    simulateDelete('batches', 'batchId', 'Batch deleted.')
  ),

  get(`${CREDITDETAILS}/stock-prices`, () => TOUR_STOCK_PRICES),
  get(CREDITDETAILS, ctx =>
    listTourData(
      live('creditingDetails', TOUR_CREDITING_DETAILS, 'id'),
      ctx.query,
      {
        searchKeys: ['batchNo', 'batchName', 'statusName', 'createdByName'],
      }
    )
  ),
  post(
    `${CREDITDETAILS}/submissions`,
    simulateAcknowledge('Crediting submitted.')
  ),
];

/* -------------------------------------------------------------------------- */
/* Award governance                                                           */
/* -------------------------------------------------------------------------- */

const governanceRoutes: TourRoute[] = [
  get(`${AWARDGOVERNANCE}/action-types`, () => TOUR_GOVERNANCE_ACTION_TYPES),
  get(`${AWARDGOVERNANCE}/employees`, ctx => {
    const rows = searchTourData(
      [...TOUR_GOVERNANCE_EMPLOYEES],
      ctx.query.get('SearchField'),
      ['employeeName', 'employeeId', 'email']
    );
    const page = listTourData(rows, ctx.query);
    return { ...page, hasMore: false };
  }),
  get(`${AWARDGOVERNANCE}/awards`, ctx => {
    const rows = searchTourData(
      [...TOUR_GOVERNANCE_AWARDS],
      ctx.query.get('SearchField'),
      ['awardNo', 'planName', 'awardStatus']
    );
    const page = listTourData(rows, ctx.query);
    return { ...page, hasMore: false };
  }),
  get(
    `${AWARDGOVERNANCE}/vesting-schedules`,
    () => TOUR_GOVERNANCE_VESTING_SCHEDULES
  ),
  get(`${AWARDGOVERNANCE}/pending-approvals`, ctx =>
    listTourData(
      live('governanceCases', TOUR_GOVERNANCE_CASES, 'txnId').filter(item =>
        inRequestedBucket(item.status, ctx.query)
      ),
      ctx.query
    )
  ),
  get(`${AWARDGOVERNANCE}/upload-templates/:kind`, ctx =>
    tourGenericDocumentPdf(`Governance ${ctx.params.kind} upload template`)
  ),
  get(`${AWARDGOVERNANCE}/txn/:txnId`, ctx =>
    getTourGovernanceCase(ctx.params.txnId)
  ),
  get(AWARDGOVERNANCE, ctx =>
    listTourData(
      live('governanceCases', TOUR_GOVERNANCE_CASES, 'txnId'),
      ctx.query,
      {
        searchKeys: ['caseName', 'createdBy', 'status'],
      }
    )
  ),
  get(`${AWARDGOVERNANCE}/:caseId`, ctx =>
    getTourGovernanceCase(ctx.params.caseId)
  ),
  post(`${AWARDGOVERNANCE}/preview`, ctx => {
    // The Add Action table reads `preview.employees` (GovernanceTranchePreview)
    // — not `items`. Returning the rows under any other key leaves the tranche
    // table empty, and with it Submit permanently disabled.
    const body = (ctx.body ?? {}) as {
      userIds?: number[];
      awardNos?: string[];
      offset?: number;
      limit?: number;
    };

    // Narrow to what the caller actually selected, so the preview reflects the
    // chosen employees rather than the whole sample set.
    let rows = [...TOUR_GOVERNANCE_EMPLOYEE_AWARDS];
    if (body.userIds?.length) {
      const wanted = new Set(body.userIds.map(Number));
      rows = rows.filter(row => wanted.has(Number(row.userId)));
    } else if (body.awardNos?.length) {
      const wanted = new Set(body.awardNos);
      rows = rows.filter(row => wanted.has(row.awardNo));
    }

    const limit = Number(body.limit) > 0 ? Number(body.limit) : rows.length;
    const page = Math.max(0, Number(body.offset) || 0);

    return {
      summary: TOUR_GOVERNANCE_SUMMARY,
      vestingSchedules: TOUR_GOVERNANCE_VESTING_SCHEDULES,
      employees: rows.slice(page * limit, page * limit + limit),
      totalCount: rows.length,
      offset: page,
      limit,
    };
  }),
  post(`${AWARDGOVERNANCE}/upload-list`, simulateAcknowledge('List uploaded.')),
  post(
    `${AWARDGOVERNANCE}/documents`,
    simulateAcknowledge('Document attached.')
  ),
  post(
    `${AWARDGOVERNANCE}/checker-action`,
    simulateCheckerAction('governanceCases')
  ),
  post(
    AWARDGOVERNANCE,
    simulateCreate('governanceCases', 'caseName', 'Case sent for approval.')
  ),
  put(
    `${AWARDGOVERNANCE}/revise/:txnId`,
    simulateAcknowledge('Case revision submitted.')
  ),
  put(
    `${AWARDGOVERNANCE}/:caseId`,
    simulateUpdate('governanceCases', 'caseId', 'Case saved.')
  ),
];

/* -------------------------------------------------------------------------- */
/* Reports                                                                    */
/* -------------------------------------------------------------------------- */

const reportRoutes: TourRoute[] = [
  get(`${REPORTCATALOG}/reports`, ctx => {
    const types = ctx.query.get('Types')?.toLowerCase().split(',');
    let reports = [...TOUR_REPORTS];
    if (types?.length) {
      reports = reports.filter(report => {
        if (types.includes('standard') && report.isStandard) return true;
        if (types.includes('shared') && report.isShared) return true;
        if (types.includes('my') && report.isMine) return true;
        return false;
      });
    }
    reports = searchTourData(reports, ctx.query.get('Search'), [
      'name',
      'code',
      'description',
    ]);
    return {
      counts: getTourReportCounts(),
      recentlyViewed: TOUR_RECENTLY_VIEWED_REPORTS,
      reports,
    };
  }),
  get(
    `${REPORT}/business-areas/:businessAreaId/record-types`,
    ctx => TOUR_RECORD_TYPES[Number(ctx.params.businessAreaId)] ?? []
  ),
  get(`${REPORT}/business-areas`, () => TOUR_BUSINESS_AREAS),
  get(
    `${REPORT}/record-types/:recordTypeId/fields`,
    ctx => TOUR_RECORD_TYPE_FIELDS[Number(ctx.params.recordTypeId)] ?? []
  ),
  get(`${REPORT}/report-definitions/:id`, ctx =>
    getTourReportDefinition(ctx.params.id)
  ),
  get(`${REPORT}/report-definitions`, () => TOUR_REPORT_DEFINITIONS),
  get(`${REPORT}/reports/downloads`, ctx => {
    const page = listTourData(
      live('downloadedReports', TOUR_DOWNLOADED_REPORTS, 'id'),
      ctx.query,
      { searchKeys: ['reportName', 'reportCode', 'initiatedByName'] }
    );
    return {
      canViewAll: true,
      scope: ctx.query.get('scope') ?? 'all',
      ...page,
    };
  }),
  get(`${REPORT}/reports/:reportCode/filters`, () => TOUR_REPORT_FILTERS),
  get(`${REPORT}/reports/:runId/download`, () =>
    tourGenericDocumentPdf('Report Export')
  ),
  post(
    '/AwardManagement/report-definitions/preview',
    () => TOUR_REPORT_PREVIEW
  ),
  post(`${REPORT}/reports/:reportCode/data`, ctx =>
    getTourReportResult(
      (ctx.body ?? {}) as Parameters<typeof getTourReportResult>[0]
    )
  ),
  post(`${REPORT}/reports/:reportCode/export`, (ctx: TourRequestContext) => {
    // The export path reads the response as a file, so the sample export is
    // handed back as a real Blob rather than the JSON envelope.
    const format = ctx.query.get('format') ?? 'pdf';
    const body = tourGenericDocumentPdf(
      `Sample export (${format}) — Help & Tours demo data`
    );
    const base64 = body.split(',')[1] ?? '';
    const bytes = Uint8Array.from(
      atob(base64),
      char => char.codePointAt(0) ?? 0
    );
    return new Blob([bytes], { type: 'application/pdf' });
  }),
  post(
    `${REPORT}/report-definitions`,
    simulateCreate('reportDefinitions', 'code', 'Report saved.')
  ),
  put(
    `${REPORT}/report-definitions/:id`,
    simulateUpdate('reportDefinitions', 'id', 'Report saved.')
  ),
  del(
    `${REPORT}/report-definitions/:id`,
    simulateDelete('reportDefinitions', 'id', 'Report deleted.')
  ),
];

/* -------------------------------------------------------------------------- */
/* Help & Plans documents                                                     */
/* -------------------------------------------------------------------------- */

const planDocumentRoutes: TourRoute[] = [
  get(PLANDOCUMENTS, () => getTourPlanDocuments()),
  // These two answer with a URL the page opens in a new tab, so they need a
  // blob URL: a data: URI is refused as a top-level navigation.
  get(`${PLANDOCUMENTS}/user-guide/download`, () => ({
    url: tourDocumentObjectUrl('Northwind Share Plan User Guide'),
  })),
  get(`${PLANDOCUMENTS}/:documentType/:planId/download`, ctx => ({
    url: tourDocumentObjectUrl(
      `Northwind Plan ${ctx.params.documentType} document`
    ),
  })),
  post(`${PLANDOCUMENTS}/faq/upload`, simulateAcknowledge('FAQ uploaded.')),
];

/* -------------------------------------------------------------------------- */
/* Shared file upload                                                         */
/* -------------------------------------------------------------------------- */

const fileRoutes: TourRoute[] = [
  post(`${fileUpload}/upload`, simulateAcknowledge('File uploaded.')),
  post('/AwardManagement/fileUpload', simulateAcknowledge('File uploaded.')),
  get(`${fileUpload}/get-file`, () => ({
    url: tourDocumentObjectUrl('Sample attachment'),
  })),
  post(`${fileUpload}/remove-file`, simulateAcknowledge('File removed.')),
  del('/AdminManagement/fileUpload', simulateAcknowledge('File removed.')),
];

/* -------------------------------------------------------------------------- */
/* Platform admin                                                             */
/* -------------------------------------------------------------------------- */

const adminRoutes: TourRoute[] = [
  get(`${ONBOARDING}/regions`, () => TOUR_REGIONS),
  get(`${ONBOARDING}/countries`, () => TOUR_COUNTRIES),
  get(`${ONBOARDING}/timezones`, () => TOUR_TIMEZONES),
  get(DATABASE, ctx =>
    listTourData(live('databases', TOUR_DATABASES, 'databaseName'), ctx.query, {
      searchKeys: ['companyName', 'databaseName', 'tenantName'],
    })
  ),
  get(
    `${DATABASE}/:databaseName`,
    ctx =>
      TOUR_DATABASES.find(db => db.databaseName === ctx.params.databaseName) ??
      TOUR_DATABASES[0]
  ),
  del(
    `${DATABASE}/:databaseName`,
    simulateDelete('databases', 'databaseName', 'Database deleted.')
  ),
  get(ONBOARDING, ctx => {
    const status = ctx.query.get('status');
    const country = ctx.query.get('country');
    let rows = live('clients', TOUR_CLIENTS, 'clientId');
    if (status) rows = rows.filter(client => client.status === status);
    if (country) rows = rows.filter(client => client.country === country);
    return listTourData(rows, ctx.query, {
      searchKeys: ['companyName', 'companyShortName', 'country', 'status'],
    });
  }),
  get(`${ONBOARDING}/status/:clientId`, ctx => ({
    clientId: ctx.params.clientId,
    status: TOUR_PRIMARY_CLIENT.status,
    steps: [
      { name: 'Company details', status: 'Completed' },
      { name: 'Branding', status: 'Completed' },
      { name: 'Database provisioning', status: 'Completed' },
      { name: 'Domain verification', status: 'Completed' },
    ],
  })),
  get(`${ONBOARDING}/:clientId/dns-records`, () => TOUR_DNS_RECORDS),
  get(
    `${ONBOARDING}/:clientId`,
    ctx =>
      live('clients', TOUR_CLIENTS, 'clientId').find(
        client => String(client.clientId) === ctx.params.clientId
      ) ?? TOUR_PRIMARY_CLIENT
  ),
  post(
    ONBOARDING,
    simulateCreate('clients', 'companyName', 'Client onboarding started.')
  ),
  post(`${ONBOARDING}/:clientId/dns-validate`, () => ({
    verified: true,
    records: TOUR_DNS_RECORDS,
    message: 'Domain verified.',
  })),
  post(
    `${ONBOARDING}/retry/:clientId`,
    simulateAcknowledge('Onboarding retried.')
  ),
  put(
    `${ONBOARDING}/:clientId`,
    simulateUpdate('clients', 'clientId', 'Client updated.')
  ),

  get(ADMIN, ctx => {
    const status = ctx.query.get('status');
    let rows = live('adminMembers', TOUR_ADMIN_MEMBERS, 'id');
    if (status) rows = rows.filter(member => member.status === status);
    return listTourData(rows, ctx.query, {
      searchKeys: ['firstName', 'lastName', 'fullName', 'email', 'role'],
    });
  }),
  get(
    `${ADMIN}/:userId`,
    ctx =>
      live('adminMembers', TOUR_ADMIN_MEMBERS, 'id').find(
        member => String(member.id) === ctx.params.userId
      ) ?? TOUR_ADMIN_MEMBERS[0]
  ),
  post(ADMIN, simulateCreate('adminMembers', 'email', 'Admin member invited.')),
  put(
    `${ADMIN}/status/:userId`,
    simulateStatusChange('adminMembers', 'userId')
  ),
  put(
    `${ADMIN}/:userId`,
    simulateUpdate('adminMembers', 'userId', 'Admin member updated.')
  ),
  del(
    `${ADMIN}/:userId`,
    simulateDelete('adminMembers', 'userId', 'Admin member removed.')
  ),
];

let registered = false;

/**
 * Register every sample-data route. Idempotent, and called the first time a
 * request is diverted, so a session that never starts a tour never builds the
 * table. The fixtures themselves are imported statically: registration has to
 * be able to complete synchronously, because `startTour` flips tour mode and
 * navigates in the same tick and the arriving page fetches immediately.
 */
export const registerAllTourRoutes = () => {
  if (registered) return;
  registered = true;

  registerTourRoutes([
    ...userRoutes,
    ...masterRoutes,
    ...parameterRoutes,
    ...maintenanceRoutes,
    ...awardRoutes,
    ...awardListRoutes,
    ...finalAwardRoutes,
    ...templateRoutes,
    ...ledgerRoutes,
    ...policyRoutes,
    ...shareCreditRoutes,
    ...governanceRoutes,
    ...reportRoutes,
    ...planDocumentRoutes,
    ...fileRoutes,
    ...adminRoutes,
  ]);
};
